// @flow
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import Modal from '../../../../../../elements/Modal/Modal';
import classNames from 'classnames/bind';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import Loading from '../../../../../../elements/Loading/Loading';
import { FormValidator } from '../../../../../../utils/form/index';

import style from './SendForgotPasswordModal.scss';
import buttonStyle from '../../../../../../assets/styles/button.scss';
import inputStyle from '../../../../../../assets/styles/input.scss';
import formStyle from '../../../../../../assets/styles/form.scss';
import yogi from '../../../../../../assets/img/home/yogi-qempo_2.png';
import yogiError from '../../../../../../assets/img/yogi-400.png';

import type { HTTPError } from '../../../../../../error/index';
import type { FormValidationResult } from '../../../../../../utils/form';
import { resetState, sendForgotPassword } from '../../../duck';

type Props = {
  +isOpen: boolean;
  +sendForgotPassword: (email: string) => void;
  +isSendingForgotPassword?: boolean;
  +sendForgotPasswordError?: HTTPError;
  +sendForgotPasswordSuccess?: boolean;
  +handleClose: () => void;
  +resetState: () => void;
};

type State = {
  email: {
    value: string;
    errors?: string[];
  };
};

const messages = defineMessages({
  emailPlaceholder: {
    id: 'auth.email.placeholder',
  },
});


export default class SendForgotPasswordModal extends Component<Props, State> {
  formValidator: FormValidator;
  state = {
    email: {
      value: '',
    },
  };


  constructor(props: Props) {
    super(props);
    this.formValidator = new FormValidator({
      email: {
        required: 'required',
        emailFormat: 'emailFormat',
      },
    });
  }

  handleChange(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      this.setState({ [name]: { value } });
    }
  }

  handleFocus(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      this.setState({ [name]: { value } });
    }
  }

  handleBlur(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      const errors = this.formValidator.validateField(name, value);
      if (errors.length && (errors[0] === 'emailFormat')) {
        this.setState({ [name]: { value, errors } });
      } else {
        this.setState({ [name]: { value } });
      }
    }
  }

  handleSubmit(e: Event) {
    e.preventDefault();
    const { sendForgotPassword } = this.props;
    const emailValue = this.state.email.value;
    const errors = this.formValidator.validateForm({
      email: emailValue,
    });
    if (errors.length) {
      errors.forEach((error: FormValidationResult) => {
        this.setState({
          [error.field]: {
            ...this.state[error.field],
            errors: error.errors,
          },
        });
      });
    } else {
      sendForgotPassword(emailValue);
    }
  }

  builderScene() {
    const { email } = this.state;
    const { isSendingForgotPassword, sendForgotPasswordSuccess, sendForgotPasswordError } = this.props;
    if (sendForgotPasswordSuccess) {
      return <Fragment>
        <img src={yogi}
             alt="salud yogi"/>
        <FormattedMessage id="modal.sendForgotPassword.success.title">{
          text => <h1>{text}</h1>
        }</FormattedMessage>
        <FormattedMessage id="modal.sendForgotPassword.success.text">{
          text => <p>{text}</p>
        }</FormattedMessage>
      </Fragment>
    } else if (sendForgotPasswordError) {
      return <Fragment>
        <img src={yogiError}
             alt="Avocado yogi"/>
        <FormattedMessage id="httpErrors.fetch.500">{
          text => <p>{text}</p>
        }</FormattedMessage>
      </Fragment>
    } else {
      const emailError = email.errors && FormValidator.buildErrorLabel(email.errors, {
        emailFormat: 'auth.email.error.format',
        required: 'auth.email.error.required',
      });
      return (
        <form className={classNames(style.authForm, formStyle.formWrap)}
              onSubmit={this.handleSubmit.bind(this)}>
          <FormattedMessage id="modal.sendForgotPassword.title">{
            text => <h1>{text}</h1>
          }</FormattedMessage>
          <FormattedMessage id="modal.sendForgotPassword.text">{
            text => <p>{text}</p>
          }</FormattedMessage>
          <section className={classNames(formStyle.row, formStyle.inputRow, style.row)}>
            <FormattedMessage id="auth.email.label">{
              txt => (<label className={formStyle.label}>{txt}</label>)
            }</FormattedMessage>
            <input type="email"
                   className={classNames(inputStyle.input,
                     { [inputStyle.red]: email.errors && email.errors.length },
                     { [inputStyle.lightBlue]: !!email.value })}
                   onChange={this.handleChange.bind(this)}
                   onFocus={this.handleFocus.bind(this)}
                   onBlur={this.handleBlur.bind(this)}
                   disabled={isSendingForgotPassword}
                   name="email"
                   placeholder={this.context.intl.formatMessage(messages.emailPlaceholder)}/>
            {emailError}
          </section>
          <FormattedMessage id="modal.sendForgotPassword.button">{
            txt => (
              <button
                type="submit"
                disabled={isSendingForgotPassword}
                className={classNames(buttonStyle.btn, buttonStyle.lightBlue, style.submitBtn)}
                onClick={this.handleSubmit.bind(this)}>{txt}
              </button>
            )
          }</FormattedMessage>
        </form>
      )
    }
  }

  handleCloseModal() {
    const { resetState, isSendingForgotPassword, handleClose } = this.props;
    if (isSendingForgotPassword) {
      return;
    }
    resetState();
    handleClose();
  }

  render() {
    const { isSendingForgotPassword, isOpen } = this.props;
    return (
      <Modal open={isOpen}
             width={544}
             height={616}
             handleClose={this.handleCloseModal.bind(this)}>
        {isSendingForgotPassword ? <Loading type="sk-three-bounce"
                                            className={style.modalLoading}
                                            number={3}/> :
          <section className={style.sendForgotPasswordModal}>
            {this.builderScene()}
          </section>
        }
      </Modal>
    )
  }
}

SendForgotPasswordModal.propTypes = {
  isOpen: PropTypes.bool,
  sendForgotPassword: PropTypes.func.isRequired,
  isSendingForgotPassword: PropTypes.bool,
  sendForgotPasswordError: PropTypes.object,
  sendForgotPasswordSuccess: PropTypes.bool,
  handleClose: PropTypes.func.isRequired,
  resetState: PropTypes.func.isRequired,
};

SendForgotPasswordModal.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/auth/auth-modals/forgot-password-modal/components/SendForgotPasswordModal/SendForgotPasswordModal.jsx